import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import PrimaryBtn from 'src/Components/GlobalComponents/primaryBtn';
import SimpleInput from 'src/Components/SimpleInput';
import httpRequest from 'src/helpers/httpRequest';
import { IDoctorExperiences } from 'src/types/GlobalInterfaces';

interface IProps {
    previous: IDoctorExperiences[];
    onSuccess: (val: IDoctorExperiences[]) => void;
    uid: string;
};

const emptyObject = {
    role: "",
    hospitalName: "",
    startDate: "",
    endDate: "",
};

const DoctorExperiencesEdit: React.FC<IProps> = ({
    previous = [],
    onSuccess = () => { },
    uid,
}) => {
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [experiences, setExperiences] = useState<IDoctorExperiences[]>(previous.length ? previous : [emptyObject]);

    const handleChange = (key: keyof IDoctorExperiences, value: string, index: number): void => {
        setExperiences(
            prev =>
                prev.map((v, i) => i === index ? { ...v, [key]: value } : v)
        );
    };

    const handleAddOrRemove = (index?: number) => {
        if (index || index === 0) { // if index include the treat like a remove
            setExperiences(
                prev =>
                    prev.filter((_, i) => i !== index)
            );
        } else {
            setExperiences(prev => [...prev, emptyObject]);
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setIsLoader(true);
        const { res, err } = await httpRequest<{ message: string; data: IDoctorExperiences[] }>({
            method: "post",
            path: `/provider/admin/experience/${uid}`,
            params: { experiences }
        });
        if (res) {
            const { message, data } = res;
            toast("Work history updated", { type: "success" });
            onSuccess(data);
        } else {
            toast(err, { type: "error" }); // api error
        }
        setIsLoader(false);
    };
    return (
        <div className="parent-box minheight-parent-box">
            <p className="font-Poppins-semiBold font-size-18px theme-color-green mb-2">Enter your work history and experience.</p>
            <form onSubmit={handleSubmit}>
                {
                    experiences.map((item: IDoctorExperiences, index: number) => (
                        <div key={index}>
                            <SimpleInput
                                value={item.role}
                                onChange={(val) => handleChange("role", val, index)}
                                isLabel={true}
                                labelText={"Job title/role"}
                                isRequired={true}
                                maxLength={120}
                                extraStyle={{ borderColor: "#009d91" }}
                            />
                            <SimpleInput
                                value={item.hospitalName}
                                onChange={(val) => handleChange("hospitalName", val, index)}
                                isLabel={true}
                                labelText={"Hospital Name"}
                                isRequired={true}
                                maxLength={120}
                                extraStyle={{ borderColor: "#009d91" }}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <SimpleInput
                                        value={moment(item.startDate).format("YYYY-MM-DD")}
                                        onChange={(val) => handleChange("startDate", `${val}T00:00:00Z`, index)}
                                        isLabel={true}
                                        labelText={"Start Date"}
                                        isRequired={true}
                                        extraStyle={{ borderColor: "#009d91" }}
                                        type='date'
                                        max={moment().format("YYYY-MM-DD")}  // Disable future dates for start date
                                    />
                                </div>
                                <div>
                                    <SimpleInput
                                        value={moment(item.endDate).format("YYYY-MM-DD")}
                                        onChange={(val) => handleChange("endDate", `${val}T00:00:00Z`, index)}
                                        isLabel={true}
                                        labelText={"End Date"}
                                        isRequired={true}
                                        extraStyle={{
                                            borderColor: "#009d91",
                                            cursor: item?.startDate ? "pointer" : "not-allowed"
                                        }}
                                        type='date'
                                        min={moment(item.startDate).format("YYYY-MM-DD")}  // Set min date for end date to start date
                                        max={moment().format("YYYY-MM-DD")}  // Disable future dates for end date
                                        disabled={!item.startDate}  // Disable end date input until start date is selected
                                        title={item.startDate ? "" : "Please select the start date first."} // if disable then show title
                                    />
                                </div>
                            </div>
                            <>
                                <div className="flex justify-end">
                                    <button
                                        className="flex gap-2 items-center"
                                        onClick={() => handleAddOrRemove(index)}
                                        type="button"
                                    >
                                        <FontAwesomeIcon className='font-size-18px' icon={faTrash} />
                                        <span className="font-Poppins-Medium font-size-16px">Delete Work History</span>
                                    </button>
                                </div>
                                <hr className='my-5' style={{ border: '1px solid #C8C8C8' }} />
                            </>
                        </div>
                    ))
                }
                <div className="flex justify-end mt-5">
                    <PrimaryBtn
                        loader={isLoader}
                        type="submit"
                        ButtonText="Save"
                        btnExtraStyle={{
                            borderRadius: "1.5rem",
                            backgroundColor: "#004540"
                        }}
                    />
                </div>
            </form>
            <div className="my-5">
                <button
                    onClick={() => handleAddOrRemove()}
                    type="button"
                    className="font-Poppins-semiBold font-size-18px theme-color-green">
                    + Add another work history
                </button>
            </div>
        </div>
    );
};

export default DoctorExperiencesEdit;